import React, { useState } from 'react';
import { RankingSnapshot, RankingWeightsConfig } from '../services/api';
import ScoreBreakdown from './ScoreBreakdown';
import ScoreDetailsModal from './ScoreDetailsModal';
import { exportRankingToCSV } from '../utils/exportUtils';
import { calculateActiveHoursSinceSale, formatTimeFromHours } from '../utils/penaltyCalculations';
import { getActiveHoursLabel, ACTIVE_HOURS_CONFIG } from '../config/activeHours';

interface RankingTableProps {
  rankings: RankingSnapshot[];
  loading?: boolean;
  onSkuClick?: (sku: string) => void;
  showFullDetails?: boolean;
  snapshotTimestamp?: string;
  weights?: RankingWeightsConfig['weights'];
}

type SortColumn = 'position' | 'score' | 'price_impact' | 'last_sale' | 'expected_hours' | null;
type SortDirection = 'asc' | 'desc';

const RankingTable: React.FC<RankingTableProps> = ({
  rankings,
  loading = false,
  onSkuClick,
  showFullDetails = false,
  snapshotTimestamp,
  weights
}) => {
  const [selectedRanking, setSelectedRanking] = useState<RankingSnapshot | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // DEBUG: Log dos primeiros 3 produtos para análise
  React.useEffect(() => {
    if (rankings && rankings.length > 0) {
      console.log('[DEBUG RankingTable] Total de produtos:', rankings.length);
      console.log('[DEBUG RankingTable] Primeiros 3 produtos (sample):',
        rankings.slice(0, 3).map(r => ({
          sku: r.sku,
          position: r.ranking_position,
          last_sale_timestamp: r.last_sale_timestamp,
          last_sale_type: typeof r.last_sale_timestamp,
          dpd_7: r.dpd_7,
          notas: r.notas
        }))
      );

      // Contar quantos têm last_sale_timestamp
      const withTimestamp = rankings.filter(r => r.last_sale_timestamp).length;
      const withoutTimestamp = rankings.length - withTimestamp;
      console.log('[DEBUG RankingTable] Estatísticas last_sale_timestamp:', {
        total: rankings.length,
        com_timestamp: withTimestamp,
        sem_timestamp: withoutTimestamp,
        percentual_com: ((withTimestamp / rankings.length) * 100).toFixed(1) + '%'
      });
    }
  }, [rankings]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      // CRITICAL: Always use snapshot timestamp, never browser's current time
      const timestamp = snapshotTimestamp || rankings[0]?.calculation_timestamp;

      if (!timestamp) {
        alert('Erro: Timestamp do snapshot não disponível.\n\nNão é possível exportar dados sem referência temporal válida.');
        setExportLoading(false);
        return;
      }
      
      // SEMPRE buscar TODOS os dados do endpoint de export
      // Não importa quantos produtos estão visíveis na tela
      try {
        const response = await fetch(`/api/rankings/snapshot/${timestamp}/export/`);
        if (!response.ok) throw new Error('Failed to fetch export data');
        
        const data = await response.json();
        console.log(`Exportando ${data.count} produtos do snapshot ${timestamp}`);
        
        // Export todos os dados
        exportRankingToCSV(data.results, timestamp);
      } catch (fetchError) {
        console.error('Erro ao buscar dados completos para export:', fetchError);
        // Se falhar, pergunta se quer exportar apenas os dados visíveis
        const exportVisible = window.confirm(
          `Erro ao buscar dados completos.\n\n` +
          `Deseja exportar apenas os ${rankings.length} produtos visíveis na tela?\n\n` +
          `(Recomendado: Cancelar e tentar novamente)`
        );
        
        if (exportVisible) {
          exportRankingToCSV(rankings, timestamp);
        }
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Erro ao exportar dados');
    } finally {
      setExportLoading(false);
    }
  };
  const formatNumber = (value: number | null, decimals = 2): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-PT', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    });
  };

  const formatPercentage = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
  };

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return `€${value.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatLastSale = (timestamp: string | null): string => {
    if (!timestamp) {
      return 'Nunca';
    }

    try {
      const saleDate = new Date(timestamp);

      // Formato: DD/MM HH:mm (UTC)
      return saleDate.toLocaleString('pt-PT', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao processar timestamp:', error);
      return '-';
    }
  };

  const formatActiveTimeSinceSale = (timestamp: string | null): string => {
    if (!timestamp) return '-';

    // CRITICAL: Never use browser's current time (new Date())
    // Always require snapshot timestamp for accurate temporal calculations
    if (!snapshotTimestamp) return '-';

    try {
      // Calculate ACTIVE hours only (9h-22h) - used in V2.0 penalty system
      const activeHours = calculateActiveHoursSinceSale(timestamp, snapshotTimestamp);
      return formatTimeFromHours(activeHours);
    } catch (error) {
      return '-';
    }
  };

  const formatTimeSinceLastSale = (timestamp: string | null): string => {
    if (!timestamp) return '-';

    // CRITICAL: Never use browser's current time (new Date())
    // Always require snapshot timestamp for accurate temporal calculations
    if (!snapshotTimestamp) return '-';

    try {
      const saleDate = new Date(timestamp);
      const snapshotDate = new Date(snapshotTimestamp);
      const diffMs = snapshotDate.getTime() - saleDate.getTime();

      const totalMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours === 0) {
        return `${minutes}min`;
      }

      return `${hours}:${minutes.toString().padStart(2, '0')}h`;
    } catch (error) {
      return '-';
    }
  };

  const getLastSaleBadgeColor = (timestamp: string | null): string => {
    if (!timestamp) return 'bg-secondary';

    // CRITICAL: Never use browser's current time (new Date())
    // Always require snapshot timestamp for accurate temporal calculations
    if (!snapshotTimestamp) return 'bg-secondary';

    try {
      // Use ACTIVE hours for badge color - consistent with V2.0 penalty system
      const activeHours = calculateActiveHoursSinceSale(timestamp, snapshotTimestamp);

      const HOURS_PER_DAY = ACTIVE_HOURS_CONFIG.TOTAL_HOURS;
      if (activeHours < HOURS_PER_DAY) return 'bg-success';           // Verde: < 1 dia ativo
      if (activeHours < HOURS_PER_DAY * 2) return 'bg-info';          // Azul: 1-2 dias ativos
      if (activeHours < HOURS_PER_DAY * 7) return 'bg-warning';       // Amarelo: 2-7 dias ativos
      return 'bg-danger';                                              // Vermelho: > 7 dias ativos
    } catch (error) {
      return 'bg-secondary';
    }
  };

  const calculateHourlyExpectation = (dpd7: number | null): number | null => {
    if (dpd7 === null || dpd7 === undefined) return null;
    // DPD_7 é a média de vendas diárias nos últimos 7 dias
    // Dividimos pelas horas ativas por dia para obter expectativa horária
    return dpd7 / ACTIVE_HOURS_CONFIG.TOTAL_HOURS;
  };

  const formatHourlyExpectation = (dpd7: number | null): string => {
    const hourlyExp = calculateHourlyExpectation(dpd7);
    if (hourlyExp === null) return '-';
    return `${hourlyExp.toFixed(2)}/h`;
  };

  const formatTimeUntilNextSale = (dpd7: number | null): string => {
    const hourlyExp = calculateHourlyExpectation(dpd7);
    if (hourlyExp === null || hourlyExp === 0) return '-';

    // Tempo médio até próxima venda = 1 / vendas_por_hora
    const hoursUntilSale = 1 / hourlyExp;

    const hours = Math.floor(hoursUntilSale);
    const minutes = Math.round((hoursUntilSale - hours) * 60);

    if (hoursUntilSale < 1) {
      return `${minutes}min`;
    }

    return `${hours}:${minutes.toString().padStart(2, '0')}h`;
  };

  const getExpectationBadgeColor = (dpd7: number | null): string => {
    const hourlyExp = calculateHourlyExpectation(dpd7);
    if (hourlyExp === null || hourlyExp === 0) return 'bg-secondary';

    if (hourlyExp < 0.1) return 'bg-danger';      // Vermelho: < 0.1/h (muito baixo)
    if (hourlyExp < 0.3) return 'bg-warning';     // Amarelo: 0.1-0.3/h (baixo)
    if (hourlyExp < 0.7) return 'bg-info';        // Azul: 0.3-0.7/h (médio)
    return 'bg-success';                           // Verde: ≥ 0.7/h (alto)
  };

  const getMovementIcon = (priceChange: number | null) => {
    if (priceChange === null || priceChange === undefined || priceChange === 0) return null;

    if (priceChange > 0) {
      return <i className="bi bi-arrow-up text-success" title={`+${formatPercentage(priceChange)}`}></i>;
    } else {
      return <i className="bi bi-arrow-down text-danger" title={formatPercentage(priceChange)}></i>;
    }
  };

  const handleDetailsClick = (ranking: RankingSnapshot) => {
    setSelectedRanking(ranking);
    setShowDetailsModal(true);
  };

  const getRowClass = (ranking: RankingSnapshot) => {
    if (!ranking.penalty_applied) return '';

    if (ranking.penalty_applied > 1.1) return 'table-success-subtle';
    if (ranking.penalty_applied < 0.5) return 'table-danger-subtle';
    if (ranking.penalty_applied < 0.9) return 'table-warning-subtle';

    return '';
  };

  // Calculate price impact factor for a ranking
  const calculatePriceImpact = (ranking: RankingSnapshot): number => {
    const penaltyApplied = ranking.penalty_applied || 1.0;
    const penaltyV2 = ranking.penalty_v2_factor || 1.0;
    return penaltyV2 > 0 ? penaltyApplied / penaltyV2 : 1.0;
  };

  // Handle column sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort rankings
  const sortedRankings = React.useMemo(() => {
    if (!sortColumn) return rankings;

    const sorted = [...rankings].sort((a, b) => {
      let aValue: number | null = null;
      let bValue: number | null = null;

      switch (sortColumn) {
        case 'position':
          aValue = a.ranking_position;
          bValue = b.ranking_position;
          break;
        case 'score':
          aValue = a.final_score;
          bValue = b.final_score;
          break;
        case 'price_impact':
          aValue = calculatePriceImpact(a);
          bValue = calculatePriceImpact(b);
          break;
        case 'last_sale':
          if (a.last_sale_timestamp && b.last_sale_timestamp) {
            return sortDirection === 'asc'
              ? new Date(a.last_sale_timestamp).getTime() - new Date(b.last_sale_timestamp).getTime()
              : new Date(b.last_sale_timestamp).getTime() - new Date(a.last_sale_timestamp).getTime();
          }
          // Null values go to the end
          if (!a.last_sale_timestamp) return 1;
          if (!b.last_sale_timestamp) return -1;
          return 0;
        case 'expected_hours':
          aValue = calculateHourlyExpectation(a.dpd_7);
          bValue = calculateHourlyExpectation(b.dpd_7);
          break;
      }

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // Compare values
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [rankings, sortColumn, sortDirection]);

  // Sort icon component
  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
    }
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.8rem' }}></i>
      : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.8rem' }}></i>;
  };

  if (loading) {
    return (
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th colSpan={showFullDetails ? 14 : 11} className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">A carregar...</span>
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <div className="alert alert-info">
        Nenhum produto encontrado para este snapshot.
      </div>
    );
  }

  return (
    <div>
      {/* Export button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span className="text-muted">
            Visualizando: {rankings.length} produtos
            {rankings.length >= 100 && (
              <span className="text-warning ms-2">
                <i className="bi bi-exclamation-circle"></i> Lista limitada
              </span>
            )}
          </span>
        </div>
        <button 
          className="btn btn-outline-primary"
          onClick={handleExport}
          disabled={exportLoading || rankings.length === 0}
          title="Exportar TODOS os dados do snapshot para CSV (não apenas os visíveis)"
        >
          {exportLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Obtendo dados completos...
            </>
          ) : (
            <>
              <i className="bi bi-download me-2"></i>
              Exportar CSV Completo
            </>
          )}
        </button>
      </div>
      
      <div className="table-responsive">
        <table className="table table-striped table-hover">
        <thead className="table-dark">
          <tr>
            <th
              style={{ width: '80px', cursor: 'pointer' }}
              onClick={() => handleSort('position')}
              title="Ranking final calculado: posição atual real do produto"
            >
              Rank Final <SortIcon column="position" />
            </th>
            <th style={{ width: '100px' }} title="Ranking com stock: posição por score entre produtos com stock (visível apenas quando stock > 0)">
              Rank C/Stock
            </th>
            <th style={{ width: '100px' }} title="Ranking sem stock: posição simulada se produto perdesse stock (visível apenas quando stock = 0)">
              Rank S/Stock
            </th>
            <th style={{ minWidth: '280px' }}>SKU</th>
            <th style={{ minWidth: '180px' }}>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('score')}
                  title="Ordenar por score final"
                >
                  Score <SortIcon column="score" />
                </span>
                <span className="text-muted">|</span>
                <span
                  style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                  onClick={() => handleSort('price_impact')}
                  title="Ordenar por Price Impact"
                  className="text-warning"
                >
                  Price <SortIcon column="price_impact" />
                </span>
              </div>
            </th>
            <th style={{ minWidth: '380px' }} title="Componentes do score">Componentes</th>
            <th
              style={{ width: '120px', cursor: 'pointer' }}
              onClick={() => handleSort('last_sale')}
              title={`Ordenar por data/hora da última venda. Mostra tempo ativo (${getActiveHoursLabel()}) usado na penalização V2.0 e tempo absoluto total.`}
            >
              Última Venda <SortIcon column="last_sale" />
            </th>
            <th
              style={{ width: '100px', cursor: 'pointer' }}
              onClick={() => handleSort('expected_hours')}
              title={`Ordenar por expectativa de vendas por hora (DPD_7 / ${ACTIVE_HOURS_CONFIG.TOTAL_HOURS}h)`}
            >
              Exp/Hora <SortIcon column="expected_hours" />
            </th>
            {showFullDetails && (
              <>
                <th>Preço</th>
                <th>Categoria</th>
                <th>Marca</th>
              </>
            )}
            <th style={{ width: '80px' }}>Ações</th>
            <th style={{ width: '200px' }}>Notas</th>
          </tr>
        </thead>
        <tbody>
          {sortedRankings.map((ranking, index) => (
            <tr
              key={ranking.sku}
              className={`${getRowClass(ranking)} cursor-pointer`}
              onClick={() => handleDetailsClick(ranking)}
              style={{ cursor: 'pointer' }}
            >
              {/* Rank Final - Sempre visível */}
              <td className="text-center">
                <span className="badge bg-primary fs-6">
                  #{ranking.rank_atual || '-'}
                </span>
              </td>

              {/* Rank C/Stock - Sempre visível */}
              <td className="text-center">
                <span className="badge bg-success">
                  #{ranking.rank_c_stock || '-'}
                </span>
              </td>

              {/* Rank S/Stock - Sempre visível */}
              <td className="text-center">
                <span className="badge bg-warning text-dark">
                  #{ranking.rank_s_stock || '-'}
                </span>
              </td>
              <td>
                <div>
                  <span
                    className={onSkuClick ? "text-primary text-decoration-underline cursor-pointer" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkuClick?.(ranking.sku);
                    }}
                    role={onSkuClick ? "button" : undefined}
                  >
                    {ranking.sku}
                  </span>
                  {ranking.product_name && (
                    <div className="small text-muted" style={{ 
                      maxWidth: '250px',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.3'
                    }}>
                      {ranking.product_name}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div>
                  <div className="d-flex align-items-center gap-1 flex-wrap">
                    <span className="fw-bold me-2">
                      {formatNumber(ranking.final_score, 4)}
                    </span>
                    {/* Badge V2.0 Temporal (considera horário ativo) */}
                    {ranking.penalty_v2_factor && ranking.penalty_v2_factor !== 1 && (
                      <span
                        className={`badge ${ranking.penalty_v2_factor > 1 ? "bg-success" : "bg-danger"}`}
                        title={`Penalização Temporal V2.0 (horário ativo ${getActiveHoursLabel()}): ${formatPercentage((ranking.penalty_v2_factor - 1) * 100)}`}
                      >
                        {ranking.penalty_v2_factor > 1 ? '+' : ''}{formatPercentage((ranking.penalty_v2_factor - 1) * 100)}
                      </span>
                    )}
                    {/* Badge Price Impact Penalty */}
                    {(() => {
                      const penaltyApplied = ranking.penalty_applied || 1.0;
                      const penaltyV2 = ranking.penalty_v2_factor || 1.0;
                      const priceImpactFactor = penaltyV2 > 0 ? penaltyApplied / penaltyV2 : 1.0;
                      const hasElasticityData = ranking.impacto_previsto_dpd_pct !== null && ranking.impacto_previsto_dpd_pct !== 0;

                      if (Math.abs(priceImpactFactor - 1.0) > 0.001) {
                        const priceImpactPct = (priceImpactFactor - 1) * 100;
                        // Determinar cor do badge baseado na existência de dados de elasticidade
                        // - COM dados (impacto_previsto_dpd_pct != 0): verde (boost) ou amarelo (penalização)
                        // - SEM dados (impacto_previsto_dpd_pct = 0): cinzento (penalização sem dados)
                        const badgeColor = hasElasticityData
                          ? (priceImpactFactor > 1 ? "bg-success" : "bg-warning")
                          : "bg-secondary";
                        const tooltipText = hasElasticityData
                          ? `Price Impact (dados históricos): ${formatPercentage(priceImpactPct)}`
                          : `Price Impact (sem dados históricos): ${formatPercentage(priceImpactPct)}`;

                        return (
                          <span
                            className={`badge ${badgeColor}`}
                            title={tooltipText}
                          >
                            Price: {priceImpactFactor > 1 ? '+' : ''}{formatPercentage(priceImpactPct)}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {ranking.base_score && ranking.final_score && ranking.base_score !== ranking.final_score && (
                    <small className="text-muted">
                      Base: {formatNumber(ranking.base_score, 4)}
                    </small>
                  )}
                </div>
              </td>
              <td style={{ minWidth: '380px' }}>
                <ScoreBreakdown ranking={ranking} compact={true} weights={weights} />
              </td>
              <td className="text-center">
                <div>
                  <span
                    className={`badge ${getLastSaleBadgeColor(ranking.last_sale_timestamp)}`}
                    title={ranking.last_sale_timestamp ? new Date(ranking.last_sale_timestamp).toLocaleString('pt-PT', { timeZone: 'UTC' }) : 'Nunca vendeu'}
                  >
                    {formatLastSale(ranking.last_sale_timestamp)}
                  </span>
                  <div className="small mt-1">
                    {/* Horas ATIVAS (usado na penalização V2.0) */}
                    <div
                      className="text-primary"
                      style={{ fontSize: '0.75rem', fontWeight: '500' }}
                      title={`Tempo em horário ativo (${getActiveHoursLabel()}) - usado na penalização V2.0`}
                    >
                      ⏰ {formatActiveTimeSinceSale(ranking.last_sale_timestamp)}
                    </div>
                    {/* Horas ABSOLUTAS (tempo real total) */}
                    <div
                      className="text-muted"
                      style={{ fontSize: '0.7rem' }}
                      title="Tempo total desde última venda (inclui período inativo)"
                    >
                      🕐 {formatTimeSinceLastSale(ranking.last_sale_timestamp)}
                    </div>
                  </div>
                </div>
              </td>
              <td className="text-center">
                <div>
                  <span
                    className={`badge ${getExpectationBadgeColor(ranking.dpd_7)}`}
                    title={`Expectativa horária baseada em DPD_7 = ${ranking.dpd_7 !== null ? ranking.dpd_7.toFixed(2) : '-'}`}
                  >
                    {formatHourlyExpectation(ranking.dpd_7)}
                  </span>
                  <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                    {formatTimeUntilNextSale(ranking.dpd_7)}
                  </div>
                </div>
              </td>
              {showFullDetails && (
                <>
                  <td>
                    <div>
                      {formatCurrency(ranking.preco_atual)}
                      {getMovementIcon(ranking.mudanca_preco_pct)}
                    </div>
                    {ranking.preco_anterior && ranking.preco_atual !== ranking.preco_anterior && (
                      <small className="text-muted">
                        Ant: {formatCurrency(ranking.preco_anterior)}
                      </small>
                    )}
                  </td>
                  <td>
                    <small>{ranking.categoria_nome || '-'}</small>
                  </td>
                  <td>
                    <small>{ranking.marca_nome || '-'}</small>
                  </td>
                </>
              )}
              <td onClick={(e) => e.stopPropagation()}>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleDetailsClick(ranking)}
                  title="Ver detalhes completos"
                >
                  <i className="bi bi-eye"></i>
                </button>
              </td>
              <td>
                {ranking.notas && (
                  <div className="small text-muted" style={{ 
                    maxWidth: '200px', 
                    maxHeight: '40px', 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.2'
                  }}
                  title={ranking.notas}>
                    {ranking.notas}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      
      {/* Modal de detalhes */}
      <ScoreDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        ranking={selectedRanking}
        weights={weights}
      />
    </div>
  );
};

export default RankingTable;